import { useState } from 'react';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../i18n/I18nProvider';
import './copyable-id.css';

/**
 * An identifier a person may genuinely need to quote: a correlation id, a
 * Meta phone-number id, a support reference. Shown short, copied whole.
 *
 * The rule this component encodes: a GUID may live in a tooltip, behind a copy
 * button or in a URL. It is never a cell's primary text and never a badge
 * label. Where the value has a human name (an organisation, a flow), use that
 * name instead and reach for this only as the secondary affordance.
 */
export function CopyableId({ value, label, chars = 8 }: { value: string; label?: string; chars?: number }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // A denied clipboard is not worth a toast: the tooltip still shows the value.
    }
  }

  const shown = value.length <= chars ? value : `${value.slice(0, chars)}…`;
  return (
    <Tooltip label={copied ? t('common.copied') : (label ?? value)} position="top" openDelay={200}>
      <button type="button" className="rr-copy-id" onClick={(e) => { e.stopPropagation(); void copy(); }} aria-label={label ?? t('common.copy')}>
        <code>{shown}</code>
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          {copied
            ? <path d="M3.5 8.5l3 3 6-6.5" />
            : <><rect x="5.5" y="5.5" width="8" height="8" rx="1.5" /><path d="M10.5 3.5H3.5a1 1 0 0 0-1 1v7" /></>}
        </svg>
      </button>
    </Tooltip>
  );
}
